import React, { useState, useMemo, useRef } from 'react';
import {
  AddIcon,
  Avatar,
  DarkModeIcon,
  Divider,
  LightModeIcon,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Spinner,
  Switch,
  Text,
  TextFieldsIcon,
} from '@billda/design-system';
import { LogoutIcon } from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useAuthStore } from '../stores/authStore';
import { Routes as AppRoutes } from '../../../routes';
import OrganizationDisplay from '../../organisation/components/OrganizationDisplay';
import { useThemeStore } from '../../../shared/stores/themeStore';
import { useDensityStore } from '../../../shared/stores/densityStore';
import { useMultiAccountStore } from '../stores/multiAccountStore';
import { FeatureSwitchGate } from '../../../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { AccountListItem } from '../../../shared/components/auth/molecules/AccountListItem';
import { isTokenExpired } from '@billda/shared-ui';
import { LogoutOptionsDialog } from './LogoutOptionsDialog';

interface UserMenuPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export const UserMenuPopover: React.FC<UserMenuPopoverProps> = ({
  anchorEl,
  onClose,
}) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { logout, logoutAll, switchAccount } = useAuthStore();
  const { mode, toggleMode } = useThemeStore();
  const { density, toggleDensity } = useDensityStore();
  const accountsRaw = useMultiAccountStore(state => state.accounts);
  const currentAccountEmail = useMultiAccountStore(
    state => state.currentAccountEmail
  );

  const [switching, setSwitching] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Sort accounts by last used
  const accounts = useMemo(
    () =>
      accountsRaw
        .filter(account => account.email !== currentAccountEmail)
        .sort(
          (a, b) =>
            new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
        ),
    [accountsRaw, currentAccountEmail]
  );

  // Anchor the DS popover to the externally-owned anchor element.
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchorEl;

  const handleOpenLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };

  const handleCloseLogoutDialog = () => {
    setLogoutDialogOpen(false);
  };

  const handleLogout = () => {
    console.log('👤 User initiated logout from UserMenuPopover');
    setLogoutDialogOpen(false);
    onClose();
    navigate(AppRoutes.login);
    logout('manual_user_action');
  };

  const handleLogoutAll = () => {
    console.log('👤 User initiated logout all from UserMenuPopover');
    setLogoutDialogOpen(false);
    onClose();
    navigate(AppRoutes.login);
    logoutAll('manual_user_action_all_devices');
  };

  const handleSwitchAccount = async (email: string) => {
    if (email === currentAccountEmail) {
      return; // Already on this account
    }

    setSwitching(true);

    try {
      await switchAccount(email);
      onClose();
      // Page will reload with new account's data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch account:', err);
      setSwitching(false);
      // Error will be shown by the AccountListItem (expired badge)
    }
  };

  const handleAddAccount = () => {
    onClose();
    navigate(`${AppRoutes.login}?addAccount=true`);
  };

  const checkTokenExpired = (refreshToken: string): boolean => {
    const expired = isTokenExpired(refreshToken);
    return expired === true;
  };

  const open = Boolean(anchorEl);

  if (!currentUser) {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        align="end"
        data-testid="user-menu-popover"
        className="w-80 p-0"
      >
        {/* User Info Section */}
        <div className="flex items-center gap-4 p-4">
          <Avatar
            size={40}
            className="bg-surface-raised font-semibold text-content-primary"
          >
            {currentUser.initials}
          </Avatar>
          <div className="min-w-0 flex-1">
            <Text as="div" variant="body1" noWrap className="font-semibold">
              {currentUser.name}
            </Text>
            <Text as="div" variant="body2" color="secondary" noWrap>
              {currentUser.email}
            </Text>
          </div>
        </div>

        <Divider />

        {/* Organization Section */}
        <OrganizationDisplay />

        <Divider />

        {/* Account List Section - feature gated */}
        <FeatureSwitchGate
          feature={Features.Auth.IsMultiAccountManagementEnabled}
        >
          <div
            className={
              accounts.length > 3
                ? 'max-h-[320px] overflow-y-auto' // 4 items * 80px = 320px
                : 'overflow-visible'
            }
          >
            {accounts.map(account => {
              const expired = checkTokenExpired(account.refreshToken);
              return (
                <AccountListItem
                  key={account.email}
                  account={account}
                  isCurrent={false}
                  isExpired={expired}
                  onClick={handleSwitchAccount}
                  disabled={switching || expired}
                />
              );
            })}

            {/* Add Account List Item */}
            <button
              type="button"
              onClick={handleAddAccount}
              disabled={switching}
              data-testid="add-new-account-list-item"
              className="flex w-full items-center py-4 text-left outline-none transition-colors hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-focused disabled:pointer-events-none disabled:opacity-38"
            >
              <span className="mr-4 ml-4 flex size-8 items-center justify-center rounded-full bg-surface-raised text-content-accent">
                <AddIcon aria-hidden="true" className="size-5" />
              </span>
              <span className="min-w-0 flex-1 pr-4">
                <Text
                  as="span"
                  variant="body1"
                  noWrap
                  className="block font-medium"
                >
                  Add New Account
                </Text>
                <Text
                  as="span"
                  variant="body2"
                  color="secondary"
                  noWrap
                  className="block"
                >
                  Sign in with a different email
                </Text>
              </span>
            </button>
          </div>

          {switching && (
            <div className="flex items-center justify-center py-4">
              <Spinner size={20} />
              <Text variant="body2" color="secondary" className="ml-4">
                Switching account...
              </Text>
            </div>
          )}

          <Divider />
        </FeatureSwitchGate>

        {/* Theme Toggle Section */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {mode === 'light' ? (
                <LightModeIcon
                  aria-hidden="true"
                  className="size-5 text-content-secondary"
                />
              ) : (
                <DarkModeIcon
                  aria-hidden="true"
                  className="size-5 text-content-secondary"
                />
              )}
              <Text variant="body2">Dark Mode</Text>
            </div>
            <Switch
              checked={mode === 'dark'}
              onCheckedChange={toggleMode}
              data-testid="user-menu-theme-toggle"
            />
          </div>
        </div>

        <Divider />

        {/* Density Toggle Section */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TextFieldsIcon
                aria-hidden="true"
                className="size-5 text-content-secondary"
              />
              <Text variant="body2">Comfortable</Text>
            </div>
            <Switch
              checked={density === 'comfortable'}
              onCheckedChange={toggleDensity}
              data-testid="user-menu-density-toggle"
            />
          </div>
        </div>

        <Divider />

        {/* Logout Button */}
        <div className="p-2">
          <button
            type="button"
            onClick={handleOpenLogoutDialog}
            data-testid="user-menu-logout"
            className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-left type-body-md text-content-primary outline-none transition-colors hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-border-focused"
          >
            <LogoutIcon aria-hidden="true" className="size-4" />
            Sign out
          </button>
        </div>

        {/* Logout Options Dialog */}
        <LogoutOptionsDialog
          open={logoutDialogOpen}
          onClose={handleCloseLogoutDialog}
          onLogout={handleLogout}
          onLogoutAll={handleLogoutAll}
        />
      </PopoverContent>
    </Popover>
  );
};
